import { useCallback } from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';
import { TranslationHookReturn, TranslationNamespace, TranslationOptions } from '../types/i18n.types';

/**
 * Enhanced useTranslation hook with better TypeScript support and additional features
 * @param namespace - Translation namespace to use
 * @returns Enhanced translation hook with additional utilities
 *
 * `t` is wrapped in useCallback so its identity is stable across renders.
 * Without this, any consumer that lists `t` in a useCallback/useEffect/useMemo
 * dependency array gets a brand-new reference every render → effects refire
 * indefinitely (root cause of the skhcn /admin/sources continuous-fetch
 * incident, 2026-05-12; replicated patch).
 */
export const useTranslation = (namespace?: TranslationNamespace): TranslationHookReturn => {
  const { t: i18nextT, i18n } = useI18nextTranslation(namespace);

  const t = useCallback((key: string, options?: TranslationOptions): string => {
    try {
      const translationOptions: any = {};

      if (options?.count !== undefined) {
        translationOptions.count = options.count;
      }

      if (options?.context) {
        translationOptions.context = options.context;
      }

      if (options?.defaultValue) {
        translationOptions.defaultValue = options.defaultValue;
      }

      if (options?.interpolation) {
        Object.assign(translationOptions, options.interpolation);
      }

      // Support direct parameter passing (like react-i18next)
      if (options && typeof options === 'object') {
        Object.keys(options).forEach(key => {
          if (key !== 'count' && key !== 'context' && key !== 'defaultValue' && key !== 'interpolation' && key !== 'ns') {
            translationOptions[key] = (options as any)[key];
          }
        });
      }

      if (options?.ns && options.ns !== namespace) {
        translationOptions.ns = options.ns;
      }

      const result = i18nextT(key, translationOptions);

      // Handle missing translations in development
      // Temporarily disabled to reduce console logs
      // if (process.env.NODE_ENV === 'development' && result === key) {
      //   console.warn(`Missing translation for key: ${key} in namespace: ${namespace || 'common'}`);
      // }

      return typeof result === 'string' ? result : String(result);
    } catch {
      // Temporarily disabled to reduce console logs
      // console.error(`Translation error for key "${key}":`, error);
      return options?.defaultValue || key;
    }
  }, [i18nextT, namespace]);

  return {
    t,
    i18n,
    currentLanguage: (i18n.language || 'vi') as any,
    isRTL: i18n.dir() === 'rtl'
  };
};

/**
 * Hook for translating with specific namespace shorthand
 */
export const useHeaderTranslation = () => useTranslation('header');
export const useChatTranslation = () => useTranslation('chat');
export const useAdminTranslation = () => useTranslation('admin');
export const useSettingsTranslation = () => useTranslation('settings');
export const useErrorTranslation = () => useTranslation('errors');
export const useCommonTranslation = () => useTranslation('common');
export const useAuthTranslation = () => useTranslation('auth');
export const useModelsTranslation = () => useTranslation('models');

export default useTranslation;